import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Building2, Library, Beaker, Monitor, Footprints, Utensils } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const Infrastructure = () => {
    useEffect(() => {
        const sections = document.querySelectorAll('.animate-section');
        sections.forEach((section) => {
            gsap.fromTo(section, { opacity: 0, y: 50 }, {
                opacity: 1,
                y: 0,
                duration: 1,
                scrollTrigger: { trigger: section, start: 'top 80%', toggleActions: 'play none none reverse' }
            });
        });
    }, []);

    const facilities = [
        { icon: Building2, title: 'Modern Classrooms', description: 'Spacious, well-ventilated classrooms with smart boards and audio-visual systems', image: 'images/1_38.jpeg' },
        { icon: Library, title: 'Library & Resource Center', description: 'Extensive collection of books, digital resources, and quiet study areas', image: 'images/1_104.avif' },
        { icon: Beaker, title: 'Science Laboratories', description: 'Well-equipped Physics, Chemistry, and Biology labs for hands-on learning', image: 'images/1_99.jpg' },
        { icon: Monitor, title: 'Computer Labs', description: 'Latest technology with high-speed internet for digital literacy', image: 'images/1_98.jpg' },
        { icon: Footprints, title: 'Sports Facilities', description: 'Playground, basketball court, indoor games, and sports equipment', image: 'images/1_68.jpeg' },
        { icon: Utensils, title: 'Cafeteria', description: 'Hygienic cafeteria serving nutritious meals and snacks', image: 'images/1_48.jpeg' }
    ];

    return (
        <div className="min-h-screen pt-24" data-testid="infrastructure-page">
            <section className="relative py-20 overflow-hidden" style={{ backgroundImage: `url(https://images.unsplash.com/photo-1764943630631-b63aadf86e19?w=1920)`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                <div className="absolute inset-0 bg-gradient-to-br from-violet-900/90 via-purple-800/90 to-blue-900/90"></div>
                <div className="relative z-10 container mx-auto px-4 md:px-8 text-center text-white">
                    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                        <h1 className="font-outfit font-bold text-5xl md:text-7xl mb-6 text-white">Infrastructure</h1>
                        <p className="font-dm-sans text-xl md:text-2xl max-w-3xl mx-auto text-white/95">World-class facilities for comprehensive learning</p>
                    </motion.div>
                </div>
            </section>

            <section className="py-24 bg-white animate-section">
                <div className="container mx-auto px-4 md:px-8">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {facilities.map((facility, index) => (
                            <motion.div key={index} className="group relative overflow-hidden rounded-3xl bg-white shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100" whileHover={{ y: -10 }} data-testid={`facility-card-${index}`}>
                                <div className="aspect-[4/3] overflow-hidden">
                                    <img src={facility.image} alt={facility.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" />
                                </div>
                                <div className="p-6">
                                    <facility.icon className="w-10 h-10 text-primary mb-4" />
                                    <h3 className="font-outfit font-bold text-xl mb-2">{facility.title}</h3>
                                    <p className="font-dm-sans text-sm text-slate-600">{facility.description}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Infrastructure;